import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Calendar, Users, Trophy, Star } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { useOrganizerReview } from "@/hooks/useOrganizerReview";
import heroTennis from "@/assets/hero-tennis.jpg";
import heroPadel from "@/assets/hero-padel.jpg";
import heroPickleball from "@/assets/hero-pickleball.jpg";

interface MyEventCardHorizontalProps {
  event: {
    id: string;
    registrationId?: string;
    title: string;
    organizer: string;
    sport: string;
    startDate: string;
    endDate?: string | null;
    category: string;
    subcategory?: string;
    partner?: string;
    status: "upcoming" | "active" | "finished";
    imageUrl?: string;
    isLeague?: boolean;
    location?: string;
    registrationFee?: number;
    organizerId?: string;
  };
  isOwnProfile?: boolean;
}

export function MyEventCardHorizontal({ event, isOwnProfile = true }: MyEventCardHorizontalProps) {
  const navigate = useNavigate();
  const [isReviewDialogOpen, setIsReviewDialogOpen] = useState(false);
  const [rating, setRating] = useState(0);
  const [hoveredStar, setHoveredStar] = useState(0);
  const [comment, setComment] = useState("");

  const { hasReviewed, submitting, submitReview } = useOrganizerReview(event.id, event.organizerId);
  
  const getDefaultImage = (sport: string) => {
    switch (sport.toLowerCase()) {
      case 'padel': return heroPadel;
      case 'tenis': 
      case 'tennis': return heroTennis;
      case 'pickleball': return heroPickleball;
      default: return heroPadel;
    }
  };
  
  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active': return { label: 'En Curso', variant: 'default' as const, className: 'bg-green-500 hover:bg-green-600' };
      case 'upcoming': return { label: 'Próximo', variant: 'secondary' as const, className: '' };
      case 'finished': return { label: 'Finalizado', variant: 'secondary' as const, className: 'bg-gray-500 text-white hover:bg-gray-600' };
      default: return { label: status, variant: 'secondary' as const, className: '' };
    }
  };
  
  const formatDateRange = (start: string, end?: string | null) => {
    const startDate = new Date(start).toLocaleDateString('es-ES', {
      day: 'numeric', month: 'short', year: 'numeric'
    });
    if (end) {
      const endDate = new Date(end).toLocaleDateString('es-ES', {
        day: 'numeric', month: 'short'
      });
      return `${startDate} - ${endDate}`;
    }
    return startDate;
  };
  
  const getSportEmoji = (sport: string) => {
    switch (sport.toLowerCase()) {
      case 'padel': return '🎾';
      case 'tenis':
      case 'tennis': return '🎾';
      case 'pickleball': return '🏓';
      default: return '🏆';
    }
  };
  
  const statusInfo = getStatusBadge(event.status);
  const detailsPath = event.isLeague 
    ? `/league/${event.id}` 
    : `/tournament/${event.id}`;
  
  const handleClick = () => navigate(detailsPath);

  const handleSubmitReview = async () => {
    const success = await submitReview(rating, comment);
    if (success) {
      setIsReviewDialogOpen(false);
      setRating(0);
      setComment("");
    }
  };
  
  if (!isOwnProfile) {
    return (
      <Card className="p-2.5 hover:shadow-md transition-shadow cursor-pointer border-border/50" onClick={handleClick}>
        <div className="flex items-center gap-2">
          <Trophy className="h-4 w-4 text-primary flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <h3 className="font-medium text-sm truncate">{event.title}</h3>
            <p className="text-xs text-muted-foreground truncate">{event.organizer} • {event.category}</p>
          </div>
          <Badge variant={statusInfo.variant} className={`text-[10px] flex-shrink-0 ${statusInfo.className}`}>
            {statusInfo.label}
          </Badge>
        </div>
      </Card>
    );
  }
  
  return (
    <>
      <Card className="p-3 md:p-4 hover:shadow-md transition-shadow cursor-pointer border-border/50" onClick={handleClick}>
        <div className="flex gap-3 md:gap-4">
          <div className="relative flex-shrink-0">
            <Badge variant={statusInfo.variant} className={`absolute top-2 left-2 z-10 text-[10px] md:text-xs ${statusInfo.className}`}>
              {statusInfo.label}
            </Badge>
            <img src={event.imageUrl || getDefaultImage(event.sport)} alt={event.title} className="w-28 h-28 md:w-32 md:h-32 rounded-lg object-cover" />
          </div>
          
          <div className="flex-1 min-w-0 flex flex-col justify-between py-0.5">
            <div className="flex justify-between items-start gap-2">
              <div className="min-w-0">
                <h3 className="font-semibold text-sm md:text-base truncate">{event.title}</h3>
                <p className="text-xs md:text-sm text-muted-foreground truncate">{getSportEmoji(event.sport)} {event.sport}</p>
              </div>
              {event.registrationFee != null && event.registrationFee > 0 && event.status !== 'finished' && (
                <div className="text-right flex-shrink-0">
                  <span className="text-base md:text-lg font-bold text-primary">${event.registrationFee.toLocaleString('es-MX')}</span>
                  <span className="text-[10px] md:text-xs text-muted-foreground block">MXN</span>
                </div>
              )}
            </div>
            
            <div className="space-y-0.5 text-xs md:text-sm text-muted-foreground">
              <div className="flex items-center gap-1.5">
                <Calendar className="w-3 h-3 md:w-3.5 md:h-3.5 flex-shrink-0" />
                <span className="truncate">{formatDateRange(event.startDate, event.endDate)}</span>
              </div>
              {event.location && <p className="truncate">📍 {event.location}</p>}
            </div>
            
            <div className="flex flex-wrap gap-1.5 items-center">
              <Badge variant="outline" className="text-[10px] md:text-xs px-1.5 py-0">{event.category}</Badge>
              {event.partner && (
                <span className="text-xs md:text-sm text-primary font-medium flex items-center gap-1">
                  <Users className="w-3 h-3 flex-shrink-0" />
                  <span className="truncate max-w-[120px]">{event.partner}</span>
                </span>
              )}
              {event.status === 'finished' && isOwnProfile && (
                hasReviewed ? (
                  <Badge className="bg-green-100 text-green-700 border-green-200 text-[10px] md:text-xs">✓ Evaluado</Badge>
                ) : (
                  <Button variant="outline" size="sm" className="text-orange-600 border-orange-300 hover:bg-orange-50 gap-1 h-6 text-xs px-2"
                    onClick={(e) => { e.stopPropagation(); setIsReviewDialogOpen(true); }}>
                    <Star className="w-3 h-3" /> Evaluar
                  </Button>
                )
              )}
            </div>
          </div>
        </div>
      </Card>

      <Dialog open={isReviewDialogOpen} onOpenChange={setIsReviewDialogOpen}>
        <DialogContent className="max-w-sm" onClick={(e) => e.stopPropagation()}>
          <DialogHeader>
            <DialogTitle className="text-base">Evaluar organizador</DialogTitle>
            <p className="text-sm text-muted-foreground">{event.organizer} — {event.title}</p>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex justify-center gap-1">
              {[1, 2, 3, 4, 5].map((star) => (
                <button key={star} type="button" className="p-1 transition-transform hover:scale-110"
                  onMouseEnter={() => setHoveredStar(star)} onMouseLeave={() => setHoveredStar(0)}
                  onClick={() => setRating(star)}>
                  <Star className={`w-7 h-7 ${star <= (hoveredStar || rating) ? 'fill-orange-400 text-orange-400' : 'text-muted-foreground/30'}`} />
                </button>
              ))}
            </div>
            <Textarea placeholder="Comentario opcional..." value={comment} onChange={(e) => setComment(e.target.value)} className="min-h-[80px] resize-none" />
            <div className="flex gap-2 justify-end">
              <Button variant="outline" size="sm" onClick={() => setIsReviewDialogOpen(false)}>Cancelar</Button>
              <Button size="sm" disabled={rating === 0 || submitting} onClick={handleSubmitReview} className="bg-orange-500 hover:bg-orange-600 text-white">
                {submitting ? 'Enviando...' : 'Enviar'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
